import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react/dist/iconify.js';
import { Navbar } from '../common/navbar';
import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
// Loader Animation
import { Ripples } from 'ldrs/react'
import 'ldrs/react/Ripples.css'

import { useNavbarContext } from '../../contexts/NavbarContext';
import { useAlerts } from '../../contexts/AlertsContext';
import { useCallData } from '../../contexts/CallDataContext';
import { useClientsData } from '../../contexts/ClientsDataContext';
import { useDefaultDateRange } from '../../hooks/useDefaultDateRange';
import { useSearch } from '../../contexts/SearchContext';
import { EquipmentProvider } from '../../contexts/EquipmentContext';
import { useEquipment } from '../../contexts/EquipmentContext';
import { useAuthUser } from '../../contexts/AuthUserContext';
import { useCustomer } from '../../contexts/CustomerContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTickets } from '../../contexts/TicketContext';
import { useKronkOrders } from '../../contexts/KronkOrdersContext';
import { clientsData } from '../../api/clientData';
import heliosLogoLight from '../../assets/common/HCAI Light.png';
import heliosLogoDark from '../../assets/common/HCAI Dark.png';
import WeekPicker from '../calendar/WeekPicker';
import ChatbotWidget from '../chatbot-embed/ChatbotWidget';
import ChatbotButton from '../chatbot-embed/ChatbotButton';
import CustomerImpersonation from '../admin/CustomerImpersonation';

const DashboardContent = () => {
  const { expandedClients, setExpandedClients } = useNavbarContext();
  const { searchQuery, setSearchQuery, handleSearch, isSearching } = useSearch();
  const user = useAuthUser();
  const { currentCustomer } = useCustomer();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef(null);

  // Check if user has access to chatbot
  const hasChatbotAccess = () => {
    // Check if user's Auth0 org is 'kronk'
    if (user?.org === 'kronk') {
      return true;
    }
    
    // Check if admin is impersonating Kronk client
    if (user?.isHeliosAdmin && currentCustomer?.name?.toLowerCase() === 'kronk') {
      return true;
    }
    
    return false;
  };

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const isAdmin = user.role === 'helios_admin';
  const heliosLogo = theme === 'dark' ? heliosLogoDark : heliosLogoLight;

  // Get client logo based on current customer (same logic as ResponsiveMobileNav)
  const getClientLogo = () => {
    if (currentCustomer) {
      // Check multiple possible logo sources for compatibility
      if (currentCustomer.branding?.logo_url) {
        return currentCustomer.branding.logo_url;
      }
      // Fallback to logoUrl property (for admin impersonation)
      if (currentCustomer.logoUrl) {
        return currentCustomer.logoUrl;
      }
      // Fallback to branding property without logo_url (if structure is different)
      if (currentCustomer.branding && typeof currentCustomer.branding === 'string') {
        return currentCustomer.branding;
      }
      
      // Fallback to clientsData for regular client users
      const customerData = clientsData.find(client =>
        client.id === currentCustomer.id ||
        client.clientId === currentCustomer.clientId ||
        client.name.toLowerCase() === currentCustomer.name.toLowerCase()
      );
      if (customerData?.logo) {
        return customerData.logo;
      }
    }
    // Fall back to Helios logo if no client logo is found
    return heliosLogo;
  };

  const getCurrentLogo = () => {
    // For client users, always show their logo
    if (!isAdmin && currentCustomer) {
      return getClientLogo();
    }
    
    // For admin users, check if current route is admin or client page
    if (isAdmin) {
      const currentPath = location.pathname;
      
      // More specific check for admin pages - include all admin-related paths
      const isAdminPage = currentPath.includes('/app/admin') || 
                          currentPath === '/app' || 
                          currentPath.includes('/customer-bootstrap') ||
                          currentPath.includes('/settings') ||
                          currentPath.includes('/alerts') ||
                          currentPath.includes('/tickets') ||
                          currentPath.includes('/help');
                          
      // Only consider it a client page if it contains a customer ID and is not an admin page
      const isClientPage = currentPath.includes('/app/') && 
                          !isAdminPage && 
                          !currentPath.includes('/app/search');
      
      // Show client logo when on client pages and impersonating
      if (isClientPage && currentCustomer) {
        return getClientLogo();
      }
      
      // Show Helios logo when on admin pages
      if (isAdminPage) {
        return heliosLogo;
      }
    }
    
    return heliosLogo;
  };

  const getCurrentCustomerName = () => {
    // For client users, always show their name
    if (!isAdmin && currentCustomer) {
      return currentCustomer.name;
    }
    
    // For admin users, check if current route is admin or client page
    if (isAdmin) {
      const currentPath = location.pathname;
      
      // More specific check for admin pages - include all admin-related paths
      const isAdminPage = currentPath.includes('/app/admin') || 
                          currentPath === '/app' || 
                          currentPath.includes('/customer-bootstrap') ||
                          currentPath.includes('/settings') ||
                          currentPath.includes('/alerts') ||
                          currentPath.includes('/tickets') ||
                          currentPath.includes('/help');
                          
      // Only consider it a client page if it contains a customer ID and is not an admin page
      const isClientPage = currentPath.includes('/app/') && 
                          !isAdminPage && 
                          !currentPath.includes('/app/search');
      
      // Show client name when on client pages and impersonating
      if (isClientPage && currentCustomer) {
        return currentCustomer.name;
      }
      
      // Show Helios Core when on admin pages
      if (isAdminPage) {
        return "Helios Core";
      }
    }
    
    return "Helios Core";
  };

  const { refreshData: refreshCallData } = useCallData();
  const { refreshAlerts } = useAlerts();
  const { refreshClientsData } = useClientsData();
  const { refreshData: refreshEquipmentData } = useEquipment();
  const { loadTickets } = useTickets();
  const { refreshOrders: refreshKronkOrders } = useKronkOrders();
  
  // Apply user's preferred default date range to WeekPicker
  useDefaultDateRange();

  // Set up keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+K or Cmd+K to focus search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Clear search when navigating away from search page
  useEffect(() => {
    if (!location.pathname.includes('/app/search') && isSearching) {
      setSearchQuery('');
    }
  }, [location.pathname, isSearching, setSearchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 120);

    return () => clearTimeout(timer);
  }, []);

  const handleGlobalRefresh = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);

    // Dispatch global refresh event for components that need it
    window.dispatchEvent(new CustomEvent('globalRefresh'));

    // Call all refresh functions. Promise.allSettled is great for this.
    await Promise.allSettled([
      refreshCallData(),
      refreshAlerts(),
      refreshClientsData(), // Add clients data refresh
      refreshEquipmentData(), // Add equipment data refresh
      loadTickets(), // Add tickets refresh
      refreshKronkOrders(), // Add Kronk orders refresh
    ]);
    // Add a small delay to give a visual feedback of refreshing
    setTimeout(() => setIsRefreshing(false), 200);
  };

  const toggleChatbot = () => {
    setIsChatbotOpen(!isChatbotOpen);
  };

  return (
    <div className="responsive-navbar-layout">
      {/* Sidebar - Responsive positioning */}
      <div className="responsive-navbar-container">
        <Navbar 
          expandedClients={expandedClients} 
          setExpandedClients={setExpandedClients}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleSearch={handleSearch}
          searchInputRef={searchInputRef}
          isRefreshing={isRefreshing}
          handleGlobalRefresh={handleGlobalRefresh}
          showMobileMenu={showMobileMenu}
          setShowMobileMenu={setShowMobileMenu}
        />
      </div>

      {/* Page Content */}
      <main className="w-full h-full flex flex-col lg:flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Ripples
              size="150"
              speed="2.5"
              color="#00aeef" 
            />
          </div>
        ) : (
          <>
            {/* Top Controls - Desktop */}
            <div className="w-full hidden lg:flex lg:flex-row justify-between items-center p-3 lg:p-4">
              <div className='shrink-0 z-20 flex items-center mr-2 sm:mr-3'>
                <CustomerImpersonation />
              </div>
              
              <div className='shrink-0 z-20 flex items-center'>
                <WeekPicker />
              </div>

              <form
                role="search"
                className="search-bar input-textbox flex-grow min-w-0 mx-2 sm:mx-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSearch(searchQuery);
                }}
              >
                <Icon icon="mingcute:search-line" className="text-xl text-accent" />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="search-input w-full focus:outline-none"
                  placeholder="Search... (Ctrl+K)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch(e.target.value);
                    }
                  }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="text-accent hover:text-primary"
                  >
                    <Icon icon="material-symbols:close" className="text-lg" />
                  </button>
                )}
              </form>

              <button
              onClick={handleGlobalRefresh}
              disabled={isRefreshing}
                className={`
                  shrink-0 py-2 px-3 button-container flex flex-row gap-1 items-center h-fit
                `}
              >
                <Icon icon="ri:refresh-line" className={`text-2xl text-accent ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>

            {/* Top Controls - Mobile */}
            <div className="dashboard-top w-full flex lg:hidden justify-between items-center p-3 lg:p-4">
                {/* Organization Logo */}
                <div className="flex items-center">
                  <img 
                    src={getCurrentLogo()} 
                    className="h-8 w-auto object-contain" 
                    alt={`${getCurrentCustomerName()} Logo`} 
                  />
                </div>
                
                {/* Menu Button */}
                <div className="flex items-center">
                  <button
                    onClick={() => setShowMobileMenu(!showMobileMenu)}
                    className="flex items-center gap-1 px-3 py-2 button-container rounded-md text-font-primary group"
                  >
                    <Icon icon="material-symbols:menu" className="text-xl group-hover:text-primary transition-all duration-200" />
                    <span className="font-medium">Menu</span>
                  </button>
                </div>
            </div>
            <hr className="border-bgAccent" />
            <div className="flex-1 min-h-0 overflow-y-auto scrollable">
                <Outlet />
            </div>
          </>
        )}
      </main>

      {/* Chatbot Widget - Only visible for authorized users */}
      {hasChatbotAccess() && (
        <>
          {/* Temporarily commented out - will be replaced with new chatbot integration */}
          {/* <ChatbotButton onClick={toggleChatbot} isOpen={isChatbotOpen} /> */}
          <ChatbotWidget isOpen={isChatbotOpen} onClose={() => setIsChatbotOpen(false)} />
        </>
      )}
    </div>
  );
};

const DashboardLayout = () => {
  return (
    <EquipmentProvider>
      <DashboardContent />
    </EquipmentProvider>
  );
};

export default DashboardLayout;
